import { Check, Delete, Edit } from '@mui/icons-material'
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import KanseiButton from 'components/button'
import CheckboxGroup from 'components/checkBoxGroup'
import KanseiDialog from 'components/dialog'
import KanseiTextField from 'components/textField'
import { properNounApi } from 'hooks/api/properNoun'
import { Option } from 'types/option'
import {
  APPLICABLE_INDEX_AI_RECORD,
  APPLICABLE_INDEX_SUMMARY,
  APPLICABLE_TO_SIZE,
  ProperNoun,
} from 'types/properNoun'

interface Form {
  writtenForm: string
  reading: string
  description: string
  flags: {
    aiRecord: boolean
    summary: boolean
  }
}

const FLAGS_OPTIONS: Option[] = [
  { value: 'aiRecord', label: '記録起こしで使用' },
  { value: 'summary', label: 'サマリ作成で使用' },
]

const defaultForm: Form = {
  writtenForm: '',
  reading: '',
  description: '',
  flags: { aiRecord: true, summary: true },
}

const formFromProperNoun = (n: ProperNoun): Form => ({
  writtenForm: n.writtenForm,
  reading: n.reading,
  description: n.description,
  flags: {
    aiRecord: n.applicableTo[APPLICABLE_INDEX_AI_RECORD] ?? false,
    summary: n.applicableTo[APPLICABLE_INDEX_SUMMARY] ?? false,
  },
})

const formToPayload = (form: Form) => {
  const flags = new Array<boolean>(APPLICABLE_TO_SIZE).fill(false)
  flags[APPLICABLE_INDEX_AI_RECORD] = form.flags.aiRecord
  flags[APPLICABLE_INDEX_SUMMARY] = form.flags.summary
  return {
    writtenForm: form.writtenForm,
    reading: form.reading,
    description: form.description,
    applicableTo: flags,
  }
}

export function ProperNounTab() {
  const [properNouns, setProperNouns] = useState<ProperNoun[]>([])
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Form>({ defaultValues: defaultForm })

  const reload = () => {
    properNounApi()
      .getAll()
      .then((res) => {
        setProperNouns(res.data || [])
      })
      .catch((error) => {
        console.error('Error fetching proper nouns:', error)
        alert('固有名詞の取得に失敗しました。')
      })
  }

  useEffect(() => {
    reload()
  }, [])

  const openCreate = () => {
    setEditingId(null)
    reset(defaultForm)
    setDialogOpen(true)
  }

  const openEdit = (n: ProperNoun) => {
    setEditingId(n.id)
    reset(formFromProperNoun(n))
    setDialogOpen(true)
  }

  const closeDialog = () => {
    setDialogOpen(false)
  }

  const onSubmit = (form: Form) => {
    if (!form.writtenForm.trim()) {
      alert('正式表記は必須です。')
      return
    }
    const payload = formToPayload(form)
    const action = editingId
      ? properNounApi().edit(editingId, payload)
      : properNounApi().add(payload)
    action
      .then(() => {
        closeDialog()
        reload()
      })
      .catch((error) => {
        console.error('Error saving proper noun:', error)
        alert('保存に失敗しました。')
      })
  }

  const handleDelete = (n: ProperNoun) => {
    if (!confirm(`「${n.writtenForm}」を削除しますか？`)) return
    properNounApi()
      .remove(n.id)
      .then(() => {
        reload()
      })
      .catch((error) => {
        console.error('Error deleting proper noun:', error)
        alert('削除に失敗しました。')
      })
  }

  return (
    <Box>
      <KanseiButton onClick={openCreate}>新規作成</KanseiButton>
      <TableContainer component={Paper} sx={{ marginTop: '20px' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>操作</TableCell>
              <TableCell>正式表記</TableCell>
              <TableCell>読み</TableCell>
              <TableCell>説明</TableCell>
              <TableCell align="center">記録起こし</TableCell>
              <TableCell align="center">サマリ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {properNouns.map((n) => (
              <TableRow key={n.id}>
                <TableCell>
                  <IconButton
                    color="primary"
                    onClick={() => openEdit(n)}
                    size="small"
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(n)}
                    size="small"
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
                <TableCell>{n.writtenForm}</TableCell>
                <TableCell>{n.reading || '-'}</TableCell>
                <TableCell>{n.description || '-'}</TableCell>
                <TableCell align="center">
                  {n.applicableTo[APPLICABLE_INDEX_AI_RECORD] ? (
                    <Check fontSize="small" color="success" />
                  ) : null}
                </TableCell>
                <TableCell align="center">
                  {n.applicableTo[APPLICABLE_INDEX_SUMMARY] ? (
                    <Check fontSize="small" color="success" />
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <KanseiDialog open={dialogOpen} handleClose={closeDialog} maxWidth="sm">
        <Stack spacing={2}>
          <Box sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
            {editingId ? '固有名詞の編集' : '固有名詞の追加'}
          </Box>
          <KanseiTextField
            id="writtenForm"
            control={control}
            label="正式表記"
            required
            size="small"
            errors={errors}
          />
          <KanseiTextField
            id="reading"
            control={control}
            label="読み（任意）"
            size="small"
            errors={errors}
          />
          <KanseiTextField
            id="description"
            control={control}
            label="説明"
            size="small"
            multiline
            rows={2}
            errors={errors}
          />
          <Box>
            <Box sx={{ marginBottom: '4px' }}>使用する処理</Box>
            <CheckboxGroup
              control={control}
              name="flags"
              options={FLAGS_OPTIONS}
            />
          </Box>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <KanseiButton variant="outlined" onClick={closeDialog}>
              キャンセル
            </KanseiButton>
            <KanseiButton onClick={handleSubmit(onSubmit)}>保存</KanseiButton>
          </Stack>
        </Stack>
      </KanseiDialog>
    </Box>
  )
}
